import * as d3 from 'd3';
import { Prediction } from '../../../types/predictions';
import { Theme } from '../../../theme';
import { formatPrice } from '../../../utils/priceUtils';

interface RenderProps {
  chartArea: d3.Selection<SVGGElement, unknown, null, undefined>;
  predictions: Prediction[];
  activePredictions: number[];
  xScale: d3.ScaleTime<number, number>;
  yScale: d3.ScaleLinear<number, number>;
  theme: Theme;
}

export const renderRangePredictions = ({
  chartArea,
  predictions,
  activePredictions,
  xScale,
  yScale,
  theme
}: RenderProps): void => {
  // Create a group for range predictions if it doesn't exist
  let rangeGroup = chartArea.select<SVGGElement>('.range-predictions-group');
  if (rangeGroup.empty()) {
    rangeGroup = chartArea.append('g')
      .attr('class', 'range-predictions-group');
  }

  // Add SVG filter definitions for glow effects (if not already present)
  const svg = chartArea.node()?.ownerSVGElement;
  if (svg) {
    let defs = d3.select(svg).select<SVGDefsElement>('defs');
    if (defs.empty()) {
      defs = d3.select(svg).append('defs');
    }
    
    // Green glow filter for successful predictions
    if (defs.select('#success-glow').empty()) {
      const successFilter = defs.append('filter')
        .attr('id', 'success-glow')
        .attr('x', '-50%')
        .attr('y', '-50%')
        .attr('width', '200%')
        .attr('height', '200%');
      
      successFilter.append('feGaussianBlur')
        .attr('in', 'SourceGraphic')
        .attr('stdDeviation', '4')
        .attr('result', 'blur');
      
      successFilter.append('feFlood')
        .attr('flood-color', '#4CAF50')
        .attr('flood-opacity', '0.6')
        .attr('result', 'color');
      
      successFilter.append('feComposite')
        .attr('in', 'color')
        .attr('in2', 'blur')
        .attr('operator', 'in')
        .attr('result', 'glow');
      
      successFilter.append('feMerge')
        .selectAll('feMergeNode')
        .data(['glow', 'SourceGraphic'])
        .enter()
        .append('feMergeNode')
        .attr('in', d => d);
    }
  }

  // Filter range predictions - only include predictions that have all required data
  const rangeData = predictions.filter(p => 
    p.prediction_type === 'range' && 
    p.start_date && 
    p.end_date && 
    p.price_min != null && 
    p.price_max != null
  );

  // Create a unique key function for data joining
  const keyFn = (d: Prediction) => `range-${d.id}`;
  
  // Range predictions background rectangles
  const predictionRects = rangeGroup.selectAll<SVGRectElement, Prediction>('.prediction-range')
    .data(rangeData, keyFn);
  
  // Enter new predictions
  const enterRects = predictionRects.enter()
    .append('rect')
    .attr('class', 'prediction-range')
    .attr('data-id', d => d.id)
    .attr('stroke-width', 1);
  
  // Update rectangles (both new and existing)
  const allRects = enterRects.merge(predictionRects as any)
    .attr('x', d => xScale(new Date(d.start_date!)))
    .attr('width', d => {
      const start = xScale(new Date(d.start_date!));
      const end = xScale(new Date(d.end_date!));
      return Math.max(1, end - start); // Ensure minimum width of 1px
    })
    .attr('y', d => yScale(Number(d.price_max)))
    .attr('height', d => {
      const maxY = yScale(Number(d.price_max));
      const minY = yScale(Number(d.price_min));
      return Math.max(1, minY - maxY); // Ensure minimum height of 1px
    })
    // Use status-based fill colors with green overlay for success
    .attr('fill', (d, i) => {
      // Success predictions get a green glow overlay
      if (d.success_status === 'success') {
        return 'rgba(76, 175, 80, 0.25)';  // Green overlay for success
      }
      // Miss predictions get a subtle red tint
      if (d.success_status === 'miss') {
        return 'rgba(244, 67, 54, 0.12)';  // Red tint for miss
      }
      // Pending predictions use sophisticated color palette
      const sophisticatedColors = [
        'rgba(247, 147, 26, 0.15)',   // Bitcoin orange, very transparent
        'rgba(92, 170, 199, 0.12)',   // Soft blue, very transparent
        'rgba(133, 186, 166, 0.12)',  // Soft green, very transparent
        'rgba(172, 142, 187, 0.12)',  // Soft purple, very transparent
        'rgba(199, 162, 118, 0.12)'   // Soft gold, very transparent
      ];
      // Use the id to deterministically select a color from the palette
      return sophisticatedColors[d.id % sophisticatedColors.length];
    })
    // Use thin, elegant borders with higher opacity for contrast
    .attr('stroke-width', d => d.success_status === 'success' ? 2 : 0.8)
    // Apply glow filter to successful predictions
    .attr('filter', d => d.success_status === 'success' ? 'url(#success-glow)' : null);
  
  // Set visibility based on active state
  allRects.classed('prediction-active', d => activePredictions.includes(d.id))
    .classed('prediction-inactive', d => !activePredictions.includes(d.id))
    // Add status coloring with transparent strokes
    .attr('stroke', d => {
      if (d.success_status === 'success') return 'rgba(76, 175, 80, 0.7)';  // Success green, semi-transparent
      if (d.success_status === 'miss') return 'rgba(244, 67, 54, 0.7)';   // Failure red, semi-transparent
      return 'rgba(247, 147, 26, 0.6)';  // Default Bitcoin orange, semi-transparent
    })
    .attr('stroke-dasharray', d => d.success_status ? null : '2,2'); // Thinner, more elegant dashed border for pending
  
  // Remove old predictions
  predictionRects.exit().remove();

  // Range labels
  const rangeLabels = rangeGroup.selectAll<SVGTextElement, Prediction>('.range-label')
    .data(rangeData, keyFn);
  
  // Enter new labels
  const enterLabels = rangeLabels.enter()
    .append('text')
    .attr('class', 'range-label')
    .attr('data-id', d => d.id)
    .attr('text-anchor', 'middle')
    .attr('font-size', '14px')
    .attr('dominant-baseline', 'text-before-edge') // Align text better
    .attr('pointer-events', 'none'); // Prevent labels from capturing mouse events
  
  // Update labels (both new and existing)
  const allLabels = enterLabels.merge(rangeLabels as any)
    .attr('x', d => {
      const start = xScale(new Date(d.start_date!));
      const end = xScale(new Date(d.end_date!));
      return start + (end - start) / 2;
    })
    .attr('y', d => yScale(Number(d.price_max)) - 20) // Position above the rectangle
    .attr('fill', d => {
      // Color based on status
      if (d.success_status === 'success') return theme.successText;
      if (d.success_status === 'miss') return theme.failedText; // changed from 'failed'
      return theme.text;
    })
    .text(''); // Label removed - using tweet card instead

  // Set visibility based on active state
  allLabels.classed('prediction-active', d => activePredictions.includes(d.id))
    .classed('prediction-inactive', d => !activePredictions.includes(d.id));
  
  // Remove old labels
  rangeLabels.exit().remove();

  // Add tooltips for better interaction
  allRects
    // Simple tooltip using title attribute
    .attr('title', d => {
      const name = d.influencer?.name || 'Expert';
      const min = formatPrice(Number(d.price_min));
      const max = formatPrice(Number(d.price_max));
      const dates = `${new Date(d.start_date!).toLocaleDateString()} - ${new Date(d.end_date!).toLocaleDateString()}`;
      return `${name}: ${min} - ${max} (${dates})`;
    });

  // REMOVED status indicators (orange dots) for range predictions
};
